import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile, isAdmin } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing connection...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for success/error parameters from oauth-callback redirect
        const success = searchParams.get('success');
        const errorParam = searchParams.get('error');
        const errorMessage = searchParams.get('error_message');
        const tiktokUser = searchParams.get('tiktok_user');
        const platformParam = searchParams.get('platform');
        const userId = searchParams.get('user_id');

        // Detect platform from params
        const platformName = platformParam === 'instagram' ? 'Instagram' : 'TikTok';
        const handle = tiktokUser || 'Unknown';

        console.log('OAuth callback params:', { success, error: errorParam, handle, platform: platformName });

        // Handle success case
        if (success === 'true') {
          console.log(`${platformName} OAuth completed successfully`);
          setStatus('success');
          setMessage(`Successfully connected ${platformName} account: @${handle}`);

          toast({
            title: `${platformName} Connected!`,
            description: `Your ${platformName} account @${handle} has been successfully connected.`,
          });

          // Check if this is a reviewer admin session and redirect accordingly
          const role = (profile as any)?.role;
          const roleHome =
            role === 'business' ? '/business/dashboard' :
            role === 'creator' || role === 'demo' ? '/creator/dashboard' :
            '/';
          const redirectPath = isAdmin ? '/reviewer-admin' : roleHome;
          
          setTimeout(() => {
            navigate(redirectPath);
          }, 2000);

          return;
        }

        // Handle error case
        if (errorParam === 'true') {
          throw new Error(errorMessage || 'OAuth connection failed');
        }

        // Handle legacy callback format (code/state) - this should not happen with new flow
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        
        if (code || state) {
          throw new Error('Unexpected callback format - OAuth callback should be handled by edge function');
        }

        // No relevant params - redirect to the role's dashboard
        const role = (profile as any)?.role;
        const roleHome =
          role === 'business' ? '/business/dashboard' :
          role === 'creator' || role === 'demo' ? '/creator/dashboard' :
          '/';
        navigate(roleHome);

      } catch (error) {
        console.error('OAuth callback error:', error);
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        
        setStatus('error');
        setMessage(errorMessage);

        toast({
          title: 'Connection Failed',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    };

    // Only process if we have search params
    if (searchParams.toString()) {
      handleCallback();
    } else {
      // No params means direct access - redirect to the role's dashboard
      const role = (profile as any)?.role;
      const roleHome =
        role === 'business' ? '/business/dashboard' :
        role === 'creator' || role === 'demo' ? '/creator/dashboard' :
        '/';
      navigate(roleHome);
    }
  }, [searchParams, user, profile, isAdmin, navigate, toast]);

  const handleRetry = () => {
    const role = (profile as any)?.role;
    const roleHome =
      role === 'business' ? '/business/dashboard' :
      role === 'creator' || role === 'demo' ? '/creator/dashboard' :
      '/';
    const redirectPath = isAdmin ? '/reviewer-admin' : roleHome;
    navigate(redirectPath);
  };

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <XCircle className="h-12 w-12 text-red-500" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'loading':
        return 'Connecting Account...';
      case 'success':
        return 'Account Connected!';
      case 'error':
        return 'Connection Failed';
      default:
        return 'Processing...';
    }
  };

  return (
    <div data-auth-card className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getIcon()}
          </div>
          <CardTitle className="text-2xl">{getTitle()}</CardTitle>
          <CardDescription className="text-center">
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'error' && (
            <Button onClick={handleRetry} className="w-full">
              Return to Dashboard
            </Button>
          )}
          {status === 'success' && (
            <p className="text-sm text-muted-foreground">
              Redirecting to dashboard in a moment...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthCallback;